<template>
    <div class="mb-64 max-w-1700 mx-auto">
        <div class="flex">
            <div class="w-1/4 pr-16 shrink-0">
                <div
                    v-if="activeState === 'all'"
                    class="bg-white shadow p-24 rounded border trans-ease border-white transition-all duration-150 space-y-12"
                >
                    <search
                        static-id="channelsSearch"
                        v-model="search"
                        :placeholder="$t('general.search')"
                        @input="triggerFilters"
                    ></search>
                    <campaign-filters
                        with-types
                        v-model="filters"
                        :campaign="campaign"
                        @update="refreshChannels"
                    />
                </div>
            </div>
            <div class="w-2/4 bg-white shadow p-32 rounded">
                <div class="flex mb-20" v-if="!campaign.limited">
                    <segmented-button
                        class="mx-auto"
                        id="activeState"
                        :options="[
                            {label: $t('campaigns.editor.channels.recommended'), value: 'recommended'},
                            {label: $t('campaigns.editor.channels.all'), value: 'all'},
                        ]"
                        value="recommended"
                        v-model="activeState"
                        @input="handleInput"
                    />
                </div>

                <pagination :data="pagination"
                            :on-each-side="1"
                            :show-next-prev-icon-only="true"
                            disable-url-update
                            class="mb-24"
                            @paginateTo="getChannelsByPage"
                ></pagination>

                <loading v-if="performingRequest" class="mb-16 p-32"/>
                <div v-for="(channel, i) in shownChannels"
                     v-else-if="activeState !== 'all' || !fetchingFirstChannels"
                     :class="[channel.highlighted[locale] ? 'border-2 border-secondary' : 'border ' + (isChannelSelected(channel) ? 'border-secondary' : 'border-white hover:border-primary-20')]"
                     class="bg-white shadow p-24 pb-0 rounded trans-ease hover:shadow transition-all duration-150 relative mb-16 cursor-pointer"
                     @click="channelSelected(channel)">
                    <div v-if="channel.highlighted[locale]" class="absolute bg-secondary font-semibold text-white leading-none py-4 px-8 rounded text-body-sm"
                         style="right: 50px; top: 0; transform: translateY(-50%)">
                        {{ $t('campaigns.editor.channels.highlighted') }}
                    </div>
                    <div class="flex items-center justify-between mb-8">
                        <div class="space-y-12">
                            <p class="text-body font-semibold">
                                {{ channel.name }}
                            </p>
                            <p class="flex items-center gap-16">
                                <price :item="channel"/>

                                <span class="inline-flex items-center">
                                    <span class="text-primary-40">
                                        <svg-icon class="max-w-16 max-h-16 mr-4" type="schedule"></svg-icon>
                                    </span>
                                    <span class="text-primary-80">
                                        {{ parseInt(channel.days_online) }}
                                        {{ $t('campaigns.days') }}
                                    </span>
                                </span>
                                <span class="inline-flex items-center relative">
                                    <svg-icon v-for="i in 5" :key="`stars-${i}`" class="h-16 w-16 text-primary-20" type="star"/>
                                    <span :style="channel.ranking || channel.ranking === 0  ? `width: ${channel.ranking}%` : `width: 100%`" class="absolute inset-0 w-full h-full flex items-center overflow-hidden">
                                        <svg-icon v-for="i in 5" :key="`selected-stars-${i}`" class="h-16 w-16 text-semantic-warning" type="star"/>
                                    </span>
                                </span>
                            </p>
                            <div class="flex items-center gap-12">
                                <div v-if="channel.suggestion_score && activeState !== 'all'">
                                    <div class="bg-secondary text-white rounded-32 px-8 py-4">
                                        {{ parseInt(parseFloat(channel.suggestion_score) * 100) + '%' }}
                                    </div>
                                </div>
                                <img :src="channel.logo_url" :alt="channel.name" style="height: 28px">
                                <simple-button
                                    :href="parseUrl(channel.url[locale])"
                                    icon="open-in-new"
                                    look="text"
                                    target="_blank"
                                    type="link"
                                    v-if="channel.url[locale]"
                                >
                                    {{ channel.is_url_example ? $t('campaigns.editor.channels.visit_example') : $t('campaigns.editor.channels.visit_site') }}
                                </simple-button>
                            </div>
                        </div>
                        <checkbox :checked="isChannelSelected(channel)"
                                  class="mr-12 flex items-center"
                                  @change="channelSelected(channel)"
                        ></checkbox>
                    </div>

                    <div
                        class="text-primary-80 trans-ease-slow overflow-hidden mt-8"
                        :class="showMoreInfoFor !== channel.id ? 'max-h-0' : 'max-h-320'"
                    >
                        <p ref="summary" v-html="channel.summary"/>
                    </div>
                    <simple-button
                        class="mx-auto mb-12"
                        icon="keyboard-arrow-down"
                        look="text"
                        v-if="showMoreInfoFor !== channel.id"
                        @click="setShowMoreInfo(channel)"
                    >
                        {{ $t('general.read_more') }}
                    </simple-button>
                </div>
                <pagination :data="pagination"
                            :on-each-side="1"
                            :show-next-prev-icon-only="true"
                            :update-url="false"
                            class="my-24"
                            @paginateTo="getChannelsByPage"
                ></pagination>
            </div>
            <div class="w-1/4 pl-16 shrink-0">
                <div class="sticky top-0">
                    <div class="bg-white shadow p-24 rounded border trans-ease border-white transition-all duration-150 space-y-24">
                        <h2 class="font-semibold text-body">
                            {{ $t('campaigns.editor.channels.selected') }} ({{ selectedChannels.length }})
                        </h2>
                        <div>
                            <h3 class="mb-8 font-semibold">
                                {{ $t('campaigns.editor.channels.estimated') }}
                            </h3>
                            <div class="flex justify-between items-center">
                                <p class="text-primary-80">{{ $t('campaigns.editor.channels.clicks') }}</p>
                                <p class="font-semibold text-body">{{ totalClicks }}</p>
                            </div>
                            <div class="flex justify-between items-center">
                                <p class="text-primary-80">{{ $t('campaigns.editor.channels.apply_clicks') }}</p>
                                <p class="font-semibold text-body">{{ totalClicksApply }}</p>
                            </div>
                        </div>
                        <div v-if="selectedChannels.length > 0" class="mb-16">
                            <h3 class="mb-8 font-semibold">
                                {{ $t('campaigns.editor.channels.title') }}
                            </h3>
                            <div v-for="channel in selectedChannels" :key="channel.id"
                                 class="border-b-2 border-primary-20 relative group overflow-hidden">
                                <div class="py-8">
                                    <div class="flex items-center justify-between">
                                        <p class="flex-1 font-semibold">{{ channel.name }}</p>
                                        <p class="text-primary-80 font-semibold inline-flex items-center">
                                            <currency/>
                                            {{ formatPrice(channel.price) }}
                                        </p>
                                    </div>
                                    <p>{{ channel.clicks_expected ? `${channel.clicks_expected} clicks` : $t('campaigns.editor.channels.no_data') }}</p>
                                    <p>{{ channel.clicks_apply_expected ? `${channel.clicks_apply_expected} clicks apply` : $t('campaigns.editor.channels.no_data') }}</p>
                                </div>
                                <div class="absolute bg-semantic-error text-white flex items-center justify-center right-0 top-0 h-full w-0 group-hover:w-32 transition-all duration-150 opacity-0 group-hover:opacity-85 cursor-pointer"
                                     @click="channelSelected(channel)">
                                    <svg-icon class="h-16 w-16" type="delete"/>
                                </div>
                            </div>
                            <div class="mt-16 text-right">
                                <p class="text-body font-semibold">{{ $t('campaigns.editor.channels.total') }}</p>
                                <p class="inline-flex items-center font-semibold"><currency/> {{ totalPrice }}</p>
                            </div>
                            <simple-button
                                class="w-full mt-24"
                                display="secondary"
                                icon="save"
                                :loading="saving"
                                look="primary"
                                @click="save('next')"
                            >{{ $t('form.submit') }}
                            </simple-button>
                        </div>
                    </div>

                    <p class="mt-16 text-primary-80 sticky text-body-sm pl-2 top-0">{{ $t('general.prices_excl_vat', { vat_label: vatLabelForRegion }) }}</p>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import Campaign from '../../../models/Campaign';
import SvgIcon from '../SvgIcon';
import Checkbox from '../../forms/Checkbox';
import Form from '../../../etc/forms/Form';
import SimpleButton from '../SimpleButton';
import Search from '../Search';
import _debounce from 'lodash/debounce';
import * as channels from '../../../services/ChannelService';
import Channel from '../../../models/Channel';
import Suggestion from '../../../models/Suggestion';
import {normalizeJson} from '../../../../shared/helpers';
import Loading from '../Loading';
import {mapState} from 'pinia';
import {useRepo} from 'pinia-orm';
import {useMainStore} from '../../../state/store';
import CampaignFilters from '../CampaignFilters';
import Pagination from '../../navigation/Pagination.vue';
import Price from "../../Price.vue";
import RegionLabel from "../../../mixins/regionLabel";
import Currency from '../../Currency';
import Badge from "../Badge.vue";
import SegmentedButton from "../SegmentedButton.vue";
import EmptyState from "../EmptyState.vue";

export default {
    components: {
        EmptyState,
        SegmentedButton,
        Badge,
        Price,
        Pagination,
        CampaignFilters,
        Loading,
        Search,
        SimpleButton,
        Checkbox,
        SvgIcon,
        Currency,
    },
    props: {
        campaign: Campaign,
        saving: Boolean,
    },
    data() {
        return {
            handlerId: 1,
            form: this.generateForm(),
            activeState: this.campaign.limited ? 'all' : 'recommended',
            search: '',
            performingRequest: false,
            fetchingFirstChannels: true,
            fetchedAllChannels: false,
            showMoreInfoFor: null,
            filters: null,
            showFilters: false,
            pagination: {},
            currentPage: 1,
        };
    },
    mixins: [
        RegionLabel,
    ],
    computed: {
        ...mapState(useMainStore, ['locale']),
        channels() {
            return this.getChannels()
                .sort((a, b) => a.fetched_at - b.fetched_at);
        },
        filteredChannels() {
            return this.channels
                .filter(channel => channel.page == this.currentPage)
                .filter(c => {
                    if (this.search && !c.name?.toLowerCase().includes(this.search.toLowerCase())) {
                        return false;
                    }

                    const fields = {regions: 'regions', educations: 'educations', job_profiles: 'job_profiles', sectors: 'sector', channel_type: 'type_id'};
                    for (let channelField in fields) {
                        if (!fields.hasOwnProperty(channelField)) {
                            return;
                        }

                        const filterField = fields[channelField];
                        const filterValues = (Array.isArray(this.filters[filterField]) ? this.filters[filterField] : [this.filters[filterField]]).filter(i => i);
                        const channelValues = (Array.isArray(c[channelField]) ? c[channelField] : [c[channelField]]).filter(i => i);

                        if (filterValues.length) {
                            for (let filterValue of filterValues) {
                                if (!channelValues.includes(parseInt(filterValue))) {
                                    return false;
                                }
                            }
                        }
                    }

                    return true;
                });
        },
        suggestions() {
            return this.getChannels(true)
                .filter(c => this.campaign.suggestions.find(s => parseInt(s.channel_id) === c.id) || c.highlighted[this.locale] || c.suggested)
                .sort((a, b) => {
                    if (a.highlighted[this.locale]) {
                        return -1;
                    }

                    if (b.highlighted[this.locale]) {
                        return 1;
                    }

                    return parseFloat(b.suggestion_score) - parseFloat(a.suggestion_score);
                });
        },
        shownChannels() {
            if (this.activeState === 'recommended') {
                return this.suggestions;
            } else {
                return this.filteredChannels;
            }
        },
        allChannels() {
            const channels = Object.assign([], this.channels);
            this.suggestions.forEach(suggestion => {
                if (!channels.find(c => c.id === suggestion.id)) {
                    channels.push(suggestion);
                }
            });
            return channels;
        },
        selectedChannels() {
            return this.allChannels.filter(this.isChannelSelected);
        },
        totalClicks() {
            return this.selectedChannels.reduce((total, channel) => total + parseInt(channel.clicks_expected || '0'), 0);
        },
        totalClicksApply() {
            return this.selectedChannels.reduce((total, channel) => total + parseInt(channel.clicks_apply_expected || '0'), 0);
        },
        totalPrice() {
            return this.formatPrice(this.selectedChannels.reduce((total, channel) => total + parseFloat(channel.price || 0), 0));
        },
    },
    methods: {
        handleInput(value) {
            if (value === 'all') {
                this.fetchChannels();
            }
        },
        getChannelsByPage(pageNumber = 1) {
            this.currentPage = pageNumber;

            if (this.filteredChannels.length === 0) {
                this.fetchChannels(null, pageNumber);
            } else {
                let pageFrom = (this.pagination.meta.per_page * (pageNumber - 1)) + 1;
                this.pagination.meta.current_page = pageNumber;
                this.pagination.meta.to = pageFrom + this.pagination.meta.per_page;
                this.pagination.meta.from = pageFrom;
            }
        },
        getChannels(suggestions = false) {
            return (suggestions ? useRepo(Suggestion) : useRepo(Channel)).all()
                .map(channel => {
                    const suggestion = this.campaign.suggestions.find(suggestion => parseInt(suggestion.channel_id) === parseInt(channel.id));
                    if (channel.suggestion_score) {
                        channel.suggested = true;
                    } else if (suggestion) {
                        channel.suggestion_score = suggestion.suggestion_score;
                    }

                    channel.name = channel.name ? channel.name[this.locale] || channel.name.en : '';
                    channel.summary = channel.summary ? channel.summary[this.locale] || channel.summary.en : '';
                    return channel;
                });
        },
        generateForm() {
            const fields = [
                {name: 'step', value: 'review'},
                {name: 'channel_ids', value: []},
            ];

            if (this.campaign) {
                fields[1].value = JSON.parse(JSON.stringify(this.campaign.channels.map(i => i.id)));
            }

            return new Form(fields);
        },
        fetchSuggestions() {
            const ids = this.campaign.suggestions.map(s => s.channel_id);
            const selected_ids = this.campaign.channels.map(i => i.id);

            this.fetchChannels(ids.concat(selected_ids).filter(i => i));
        },
        fetchChannels(ids, page = 1) {

            this.performingRequest = true;

            let filters = [
                this.filters.regions ? 'filter[regions]=' + this.filters.regions.join('|') : undefined,
                this.filters.educations ? 'filter[educations]=' + this.filters.educations.join('|') : undefined,
                this.filters.job_profiles ? 'filter[job_profiles]=' + this.filters.job_profiles.join('|') : undefined,
                this.filters.sector ? 'filter[sectors]=' + this.filters.sector : undefined,
            ].filter(i => i).join('&');

            return channels.index(ids, page, this.activeState === 'all' ? this.search : undefined, this.activeState === 'all' ? filters : undefined)
                .then(result => {
                    this.pagination = result;

                    let data = normalizeJson(result.data.data);

                    if (!ids) {
                        this.fetchedAllChannels = data.length === 0;
                        return useRepo(Channel).save(data);
                    }

                    return Promise.all([
                        useRepo(Suggestion).save(
                            data.filter(c =>
                                this.campaign.suggestions.find(s => parseInt(s.channel_id) === c.id)
                                    || c.highlighted[this.locale] || c.suggestion_score
                            )
                        ),
                        useRepo(Channel).save(
                            data.filter(c =>
                                !this.campaign.suggestions.find(s => parseInt(s.channel_id) === c.id)
                                    && !c.highlighted[this.locale] && !c.suggestion_score
                            )
                        ),
                    ]);
                }).then(() => {
                    this.performingRequest = false;

                    if (!ids) {
                        this.fetchingFirstChannels = false;
                    }
                });
        },
        isChannelSelected(channel) {
            return this.form.channel_ids.includes(parseInt(channel.id));
        },
        channelSelected(channel) {
            const id = parseInt(channel.id);

            if (this.form.channel_ids.includes(id)) {
                return this.form.channel_ids = this.form.channel_ids.filter(i => i !== id);
            }

            return this.form.channel_ids.push(id);
        },

        getProbabilityColor(val) {
            val = parseFloat(val) * 100;

            if (val >= 70) {
                return 'bg-secondary';
            }
            if (val >= 40) {
                return 'bg-semantic-warning';
            }

            return 'bg-semantic-error';
        },

        save(todo) {
            this.$emit('save', {
                form: this.form,
                relationships: {
                    channels: {
                        data: this.selectedChannels.map(c => ({id: c.id})),
                    },
                },
                todo,
            });
        },

        triggerFilters: _debounce(function () {
            this.refreshChannels();
        }, 500),
        refreshChannels() {
            if (this.handlerId === 1 && this.fetchingFirstChannels) {
                return;
            }

            this.fetchingFirstChannels = true;
            this.fetchedAllChannels = false;
            this.handlerId++;
            this.fetchChannels();
        },

        formatPrice(price) {
            if (!price) {
                return price;
            }

            return price.toFixed(2).replace('.', ',');
        },
        summaryHeight(channel, i) {
            if (!this.$refs.summary || !this.$refs.summary[i]) {
                return 'height: 0';
            }

            if (this.showMoreInfoFor === channel.id) {
                return 'height: ' + getComputedStyle(this.$refs.summary[i]).height;
            } else {
                return 'height: 0';
            }
        },
        readMoreHeight(channel, i) {
            if (!this.$refs.readmore || !this.$refs.readmore[i]) {
                return null;
            }

            if (this.showMoreInfoFor !== channel.id) {
                return 'height: ' + getComputedStyle(this.$refs.readmore[i]).height;
            } else {
                return 'height: 0';
            }
        },
        setShowMoreInfo(channel) {
            this.showMoreInfoFor = channel.id;
            this.$forceUpdate();
        },
        parseUrl(url) {
            if (url.startsWith('http')) {
                return url;
            }

            return `https://${url}`;
        },
    },
    mounted() {
        this.filters = new Form([
            {name: 'regions', value: []},
            {name: 'educations', value: []},
            {name: 'sector', value: null},
            {name: 'job_profiles', value: []},
            {name: 'country_code', value: this.campaign.country_code},
            {name: 'locale', value: this.campaign.locale},
            {name: 'type_id', value: null},
        ]);

        useRepo(Channel).flush();
        useRepo(Suggestion).flush();
        this.fetchSuggestions();

        if (this.activeState === 'all') {
            this.fetchChannels();
        }
    },
};
</script>
